import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useAdjustBalance } from '../queries/consumableBalances'
import type { MessageType } from '../types/consumableBalance.types'

interface Props {
  accountId: string
  /** Pre-selected channel when opened from a specific balance row's "Adjust" button. */
  defaultChannel?: string
  onClose: () => void
}

// Matches the channel set used elsewhere in the Finance section (ConsumablesReportPage's
// CHANNEL_OPTIONS), minus the "All Channels" option — an adjustment always targets one channel.
const CHANNEL_OPTIONS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'RCS', label: 'RCS' },
  { value: 'WEB_PUSH', label: 'Web Push' },
  { value: 'MOBILE_PUSH', label: 'Mobile Push' },
]

const MESSAGE_TYPE_OPTIONS: { value: MessageType; label: string }[] = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'transactional', label: 'Transactional' },
]

export function AdjustBalanceModal({ accountId, defaultChannel, onClose }: Props) {
  const [channel, setChannel] = useState(defaultChannel ?? '')
  const [messageType, setMessageType] = useState<MessageType>('promotional')
  const [delta, setDelta] = useState('')
  const [reason, setReason] = useState('')

  const mutation = useAdjustBalance(accountId)

  const deltaValue = Number(delta)
  const isValid =
    !!channel &&
    delta.trim() !== '' &&
    !Number.isNaN(deltaValue) &&
    deltaValue !== 0 &&
    reason.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    mutation.mutate(
      {
        channel,
        messageType,
        delta: deltaValue,
        reason: reason.trim(),
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Adjust Consumable Balance</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Applies immediately and writes a permanent audit entry — there is no separate approval step.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Channel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Channel <span className="text-red-500">*</span>
            </label>
            <Select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              options={CHANNEL_OPTIONS}
              placeholder="Select a channel"
              required
              disabled={!!defaultChannel}
            />
          </div>

          {/* Message type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Balance Pool <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {MESSAGE_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="messageType"
                    value={opt.value}
                    checked={messageType === opt.value}
                    onChange={() => setMessageType(opt.value)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Delta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjustment <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="e.g. 5000 or -250"
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              Positive to credit the balance, negative to debit it. A debit is rejected if it would take the
              balance below zero.
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain what happened and why this correction is needed — this becomes the permanent audit record for this adjustment."
              maxLength={1000}
              rows={4}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? 'Applying…' : 'Apply Adjustment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
